/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/EmployeeManagement/employeeSchema.ts
 * PURPOSE: EntitySchema<Employee> instance wiring the generic MasterDataCrudPage
 *          framework (PR #407) to the existing Employee CRUD REST API — gives
 *          Employee a full Create/Read/Update/Delete screen for free.
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-09
 * COMPLIANCE: SDLC 6.3.1 + BR-PLAT-001-MASTER-DATA-CRUD-IMPORT-EXPORT-STANDARD
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * @business-context
 * - Domain: Vietnamese F&B Employee Master Data — HR/Tenant-Admin CRUD screen
 * - Impact: BR-PLAT-001 backlog item "Build Employee CRUD frontend screen"
 *   (5 SP) — API already existed with zero UI; unblocked by PR #407 (framework)
 *   and PR #408 (employees.locally_modified_at BFlow-overwrite guard).
 * - Stakeholders: @cto, HR Admins, Tenant Admins
 *
 * @technical-architecture
 * - Layer: Frontend Presentation Layer, Page-level config
 * - Pattern: Config-Driven UI (Strategy Pattern — schema supplies behavior to
 *   the shared MasterDataCrudPage container)
 * - Dependencies: frontend/src/components/master-data/MasterDataCrudPage.tsx,
 *   frontend/src/components/master-data/types.ts, frontend/src/services/api/employees.ts
 *
 * @cross-references
 * - BR: docs/01-planning/01-requirements/BR-PLAT-001-MASTER-DATA-CRUD-IMPORT-EXPORT-STANDARD.md
 * - Schema contract: frontend/src/components/master-data/types.ts
 * - API service/types: frontend/src/services/api/employees.ts
 * - Backend API: backend/app/api/v1/employees.py
 * - Backend schemas: backend/app/schemas/employee.py
 * - Page: frontend/src/pages/EmployeeManagement/index.tsx
 * - Route: frontend/src/App.tsx (/tenant-admin/employees)
 *
 * Design notes (decisions made reading the real backend, not guessed):
 *
 * 1. PII fields EXCLUDED from this MVP form (citizen_id, bhxh_number,
 *    bank_account_number, tax_code) per task scope — these are accepted as
 *    plain fields by the existing endpoints (no new encryption work needed
 *    here), but are deliberately left out of the v1 UI surface pending a
 *    dedicated PII-handling UX review (masking, audit-log confirmation
 *    already merged server-side per PR #358, but no frontend affordance yet).
 *
 * 2. `organization_id` is REQUIRED by EmployeeCreateRequest (backend/app/
 *    schemas/employee.py:190) but is SILENTLY DISCARDED server-side —
 *    backend/app/services/employee_service.py `create_employee()` does
 *    `data_dict.pop('organization_id', None)` and always uses the
 *    server-derived `current_user.organization_id` instead (backend/app/
 *    api/v1/employees.py:154-158). The field is defaulted to an all-zero
 *    placeholder UUID here so HR admins never have to think about it; the
 *    value is required only to satisfy Pydantic request validation.
 *
 * 3. `branch_id` and `work_area_id` are Optional in EmployeeCreateRequest's
 *    type annotation, but backend/app/services/employee_service.py's
 *    `create_employee()` (lines 378-390) unconditionally does
 *    `self.db.get(Branch, employee_data.branch_id)` /
 *    `self.db.get(WorkArea, employee_data.work_area_id)` and raises
 *    ValidationError when either lookup misses — i.e. a `None` branch_id/
 *    work_area_id always 400s in the current service implementation. They
 *    are therefore marked `required: true` here so HR admins get inline
 *    validation instead of a guaranteed backend 400. This looks like a
 *    latent backend schema/service mismatch worth a follow-up ticket —
 *    flagged to @reviewer, not fixed in this frontend-only task.
 *
 * 4. The generic form (MasterDataCrudPage.tsx) only supports static
 *    `FieldOption[]` for 'select' fields — there is no FK-lookup/async-fetch
 *    mechanism in the framework (confirmed by reading types.ts + the
 *    component in full). organization_id/branch_id/work_area_id are
 *    therefore plain UUID text inputs with format validation, not dropdowns
 *    populated from the Organizations API. Wiring real FK-lookup dropdowns
 *    is out of scope for this task (framework limitation, not an Employee
 *    bug) and should be a follow-up to the MasterDataCrudPage framework.
 */

import type { EntitySchema, FieldDef } from '@/components/master-data/types';
import type { ColumnDef } from '@/components/dashboard/DataTable';
import type { Employee } from '@/services/api/employees';

// Loose format-only validation for inline UX feedback. The backend remains
// the source of truth for correctness (backend/app/core/vietnamese_compliance.py).
const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const VN_PHONE_PATTERN = /^(\+84|0)\d{9,10}$/;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const validateUuid = (value: unknown): string | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  return UUID_PATTERN.test(String(value)) ? undefined : 'UUID không hợp lệ (định dạng: 8-4-4-4-12)';
};

const validateVietnamesePhone = (value: unknown): string | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  return VN_PHONE_PATTERN.test(String(value))
    ? undefined
    : 'Số điện thoại không hợp lệ (VD: 0909123456 hoặc +84909123456)';
};

const validateEmail = (value: unknown): string | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  return EMAIL_PATTERN.test(String(value)) ? undefined : 'Email không đúng định dạng';
};

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  full_time: 'Toàn thời gian',
  part_time: 'Bán thời gian',
  contract: 'Hợp đồng',
  temporary: 'Thời vụ',
  internship: 'Thực tập',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Đang làm việc',
  on_leave: 'Nghỉ phép',
  terminated: 'Đã nghỉ việc',
  suspended: 'Tạm ngưng',
};

const columns: ColumnDef<Employee>[] = [
  { key: 'employee_code', label: 'Mã NV', sortable: true, width: '110px' },
  { key: 'full_name', label: 'Họ tên', sortable: true },
  { key: 'position', label: 'Chức vụ', render: (value) => (value as string) || '—' },
  {
    key: 'branch_name',
    label: 'Chi nhánh',
    render: (value) => (value as string) || '—',
  },
  {
    key: 'contract_type',
    label: 'Loại HĐ',
    render: (value) => (value ? CONTRACT_TYPE_LABELS[value as string] || (value as string) : '—'),
  },
  {
    key: 'status',
    label: 'Trạng thái',
    align: 'center',
    render: (value) => (value ? STATUS_LABELS[value as string] || (value as string) : '—'),
  },
  {
    key: 'hire_date',
    label: 'Ngày vào làm',
    render: (value) => (value ? String(value).slice(0, 10) : '—'),
  },
];

const formFields: FieldDef[] = [
  { key: 'employee_code', label: 'Mã nhân viên', type: 'text', required: true, placeholder: 'NV001' },
  { key: 'full_name', label: 'Họ và tên', type: 'text', required: true, placeholder: 'Nguyễn Văn A' },
  {
    key: 'phone_number',
    label: 'Số điện thoại',
    type: 'text',
    required: true,
    placeholder: '0909123456',
    validate: validateVietnamesePhone,
  },
  {
    key: 'organization_id',
    label: 'Organization ID',
    type: 'text',
    required: true,
    defaultValue: '00000000-0000-0000-0000-000000000000',
    helpText:
      'Trường bắt buộc bởi API nhưng bị bỏ qua ở backend — hệ thống luôn dùng tổ chức của tài khoản đăng nhập hiện tại.',
    validate: validateUuid,
  },
  {
    key: 'branch_id',
    label: 'Chi nhánh (Branch ID)',
    type: 'text',
    required: true,
    helpText: 'UUID chi nhánh — tra cứu tại Quản Lý Tổ Chức (/tenant-admin/organizations).',
    validate: validateUuid,
  },
  {
    key: 'work_area_id',
    label: 'Khu vực làm việc (Work Area ID)',
    type: 'text',
    required: true,
    helpText: 'UUID khu vực làm việc thuộc chi nhánh đã chọn ở trên.',
    validate: validateUuid,
  },
  { key: 'position', label: 'Chức vụ', type: 'text', placeholder: 'Nhân viên phục vụ' },
  {
    key: 'contract_type',
    label: 'Loại hợp đồng',
    type: 'select',
    required: true,
    defaultValue: 'full_time',
    options: [
      { value: 'full_time', label: 'Toàn thời gian' },
      { value: 'part_time', label: 'Bán thời gian' },
      { value: 'contract', label: 'Hợp đồng' },
      { value: 'temporary', label: 'Thời vụ' },
      { value: 'internship', label: 'Thực tập' },
    ],
  },
  { key: 'hire_date', label: 'Ngày vào làm', type: 'date', required: true },
  { key: 'date_of_birth', label: 'Ngày sinh', type: 'date' },
  {
    key: 'gender',
    label: 'Giới tính',
    type: 'select',
    options: [
      { value: 'M', label: 'Nam' },
      { value: 'F', label: 'Nữ' },
      { value: 'O', label: 'Khác' },
    ],
  },
  { key: 'email', label: 'Email', type: 'text', placeholder: 'nguyen.van.a@example.com', validate: validateEmail },
  { key: 'base_salary', label: 'Lương cơ bản (VND)', type: 'number', placeholder: '8000000' },
  { key: 'permanent_address', label: 'Địa chỉ thường trú', type: 'text' },
  {
    key: 'status',
    label: 'Trạng thái làm việc',
    type: 'select',
    defaultValue: 'active',
    options: [
      { value: 'active', label: 'Đang làm việc' },
      { value: 'on_leave', label: 'Nghỉ phép' },
      { value: 'terminated', label: 'Đã nghỉ việc' },
      { value: 'suspended', label: 'Tạm ngưng' },
    ],
  },
  { key: 'is_active', label: 'Kích hoạt', type: 'boolean', defaultValue: true },
];

/**
 * BR-PLAT-001-R5 role gating. Employee CRUD is mounted under
 * /tenant-admin/employees (see App.tsx), whose TenantAdminLayout wrapper
 * already restricts page access to TENANT_ADMIN/TENANT_OWNER only (DD-0002
 * §4 — same guard Organizations/Users management use). The permissions below
 * match the backend's update/delete guards exactly (verify_role HR_ADMIN/ADMIN
 * → TENANT_OWNER/TENANT_ADMIN mapping, backend/app/api/v1/employees.py
 * `verify_role` DD-0042). Note: the backend's CREATE endpoint additionally
 * allows BRANCH_MANAGER (verify_role(["HR_ADMIN","ADMIN","BRANCH_MANAGER"]),
 * employees.py:148) — this screen doesn't expose that, since the whole route
 * is TENANT_ADMIN/TENANT_OWNER-only by the layout guard, consistent with the
 * Organizations/Users precedent. A future BRANCH_MANAGER-facing entry point
 * (if wanted) would need its own route outside TenantAdminLayout, not a
 * change to this schema.
 */
export const employeeSchema: EntitySchema<Employee> = {
  entityKey: 'employees',
  apiBasePath: '/employees',
  entityLabel: 'Nhân viên',
  columns,
  formFields,
  permissions: {
    create: ['TENANT_OWNER', 'TENANT_ADMIN'],
    edit: ['TENANT_OWNER', 'TENANT_ADMIN'],
    delete: ['TENANT_OWNER', 'TENANT_ADMIN'],
  },
  // Export bật (2026-08-09, quyết định người dùng: Export trước, Import để
  // đợt sau — rủi ro tạo/sửa nhầm nhân viên thật cao hơn, cần kiểm thử kỹ).
  // Backend GET /employees/export đã có sẵn từ trước NHƯNG bị route-order
  // bug (khai sau "/{employee_id}" nên luôn bị "/{employee_id}" nuốt mất,
  // employee_id="export" → 422) — đây là lý do THẬT nút Xuất "không hoạt
  // động", đã sửa thứ tự route trong employees.py cùng lúc với đổi này.
  importExport: {
    enabled: true,
    exportOnly: true,
    exportFileName: 'nhan-vien.csv',
    templateColumns: [
      'employee_code', 'full_name', 'email', 'phone',
      'department', 'position', 'branch', 'employment_status', 'hire_date',
    ],
  },
};

export default employeeSchema;
